import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import translationES from './locales/es.json';
import translationCA from './locales/ca.json';
import translationEN from './locales/en.json';

const resources = {
  es: { translation: translationES },
  ca: { translation: translationCA },
  en: { translation: translationEN },
};

/**
 * Política de idioma:
 * ────────────────────────────────────────────────────────────────────
 * • Desarrollo (import.meta.env.DEV): siempre castellano ("es"). El
 *   fuente única de verdad se escribe en ES; EN/CA se generan por
 *   traducción automática en el pipeline CI/CD (DeepL).
 *
 * • Producción: se respeta el idioma del usuario aplicando la regla:
 *     - navegador reporta "ca*" → ca
 *     - navegador reporta "es*" → es
 *     - cualquier otro          → en
 *
 * Nota: en producción NO usamos LanguageDetector "en crudo" porque
 * detectaría códigos como "fr", "de", "it"... y caería al fallback
 * "es", lo que contradice la política de negocio (default EN para
 * usuarios no ES/CA).
 */
const isDev = typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV === true;

/**
 * Resuelve el idioma inicial en producción a partir del navegador.
 * Devuelve siempre uno de: 'es' | 'ca' | 'en'.
 */
function resolveProdLanguage(): 'es' | 'ca' | 'en' {
  try {
    // 1) Preferencia guardada explícitamente por el usuario tiene prioridad.
    const stored = typeof window !== 'undefined'
      ? window.localStorage?.getItem('i18nextLng')
      : null;
    if (stored) {
      const s = stored.toLowerCase();
      if (s.startsWith('ca')) return 'ca';
      if (s.startsWith('es')) return 'es';
      if (s.startsWith('en')) return 'en';
      // otros idiomas almacenados → forzamos EN por política
    }

    // 2) Navegador
    const raw = (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || '';
    const lang = raw.toLowerCase();
    if (lang.startsWith('ca')) return 'ca';
    if (lang.startsWith('es')) return 'es';
    return 'en';
  } catch {
    return 'en';
  }
}

const initialLanguage = isDev ? 'es' : resolveProdLanguage();

// En desarrollo forzamos ES también en localStorage para evitar arrastres
// de sesiones anteriores del navegador.
if (isDev && typeof window !== 'undefined') {
  try { window.localStorage?.setItem('i18nextLng', 'es'); } catch { /* ignore */ }
}

const i18nInit = i18n.use(initReactI18next);

// En producción usamos LanguageDetector únicamente como *cache* (localStorage/cookie),
// pero el idioma inicial lo fijamos nosotros con `lng`. En dev no hace falta el detector.
if (!isDev) {
  i18nInit.use(LanguageDetector);
}

i18nInit.init({
  resources,
  lng: initialLanguage,
  fallbackLng: 'es', // Español como fallback (completo) mientras EN y CA se completan
  supportedLngs: ['es', 'ca', 'en'],
  interpolation: {
    escapeValue: false, // React already does escaping
  },
  detection: {
    // En producción, si el usuario cambia idioma en el selector, se cachea.
    // El primer arranque siempre lo decide `lng` calculado arriba.
    order: ['localStorage', 'cookie'],
    caches: ['localStorage', 'cookie'],
  },
});

export default i18n;
